import { FormEvent, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { isEmailValid } from 'dash-common';
import {
  Flex,
  Form,
  Input,
  Button,
  useFormState,
  useFormValidation,
  FormErrorTypes,
  ViewerContext,
  localStorage,
  useServerResponseTranslation,
} from 'dash-ui';
import {
  useAuthorizationMutation,
  useGetAccountQuery,
} from 'gql/generated.graphql';
import { RoutesPath } from 'flow-components/routes/routes';

enum FormFields {
  Email = 'email',
  Password = 'password',
}

const FormFieldsList = [FormFields.Email, FormFields.Password];

function SignInForm() {
  const { t } = useTranslation('forms');
  const { tokens, setAccount } = useContext(ViewerContext);
  const [formErrors, , [setEmailError, setPasswordError]] =
    useFormValidation(FormFieldsList);
  const navigate = useNavigate();
  const translateErrorMessage = useServerResponseTranslation();

  const [
    formState,
    [setEmail, setPassword],
    [formOnSubmit, formOnError, formOnSuccess],
  ] = useFormState(FormFieldsList);
  const [authMutation, { loading }] = useAuthorizationMutation({
    onError(error) {
      formOnError(translateErrorMessage(error));
      setEmailError({ type: FormErrorTypes.Invalid, message: '' });
      setPasswordError({ type: FormErrorTypes.Invalid, message: '' });
    },
    onCompleted: (data) => {
      const { authorization } = data;
      const { accessToken, refreshToken, refreshTokenExpiry } = authorization;
      localStorage.setAccessToken(accessToken);
      localStorage.setRefreshToken(refreshToken);
      localStorage.setRefreshTokenExpiry(refreshTokenExpiry || '');
      window.dispatchEvent(new Event('storage'));
      formOnSuccess();
    },
  });

  const { loading: isViewerLoading, data: accountData } = useGetAccountQuery({
    skip: !tokens.accessToken,
    fetchPolicy: 'no-cache',
  });
  const account = accountData?.getAccount;

  // redirect to home page effect
  useEffect(() => {
    if (account && setAccount) {
      setAccount(account);
      navigate(RoutesPath.Home);
    }
  }, [account, setAccount, navigate]);

  const validEmail = (value: string) => {
    if (!value) {
      setEmailError({
        type: FormErrorTypes.Info,
        message: t`messages.emailRequired`,
      });
      return false;
    }
    if (!isEmailValid(value)) {
      setEmailError({
        type: FormErrorTypes.Invalid,
        message: t`messages.emailInvalid`,
      });
      return false;
    }
    setEmailError(null);
    return true;
  };

  const validPassword = (value: string) => {
    if (!value) {
      setPasswordError({
        type: FormErrorTypes.Info,
        message: t`messages.passwordRequired`,
      });
      return false;
    }
    setPasswordError(null);
    return true;
  };

  const onEmailTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    validEmail(e.target.value);
  };
  const onPasswordTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setPassword(value);
    validPassword(value);
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    formOnSubmit();
    const { email, password } = formState;
    if ([validEmail(email), validPassword(password)].filter((v) => !v).length) {
      return null;
    }
    authMutation({
      variables: { email, password },
    });
    return null;
  };
  return (
    <Form onSubmit={onSubmit} w={334}>
      <Form.Group mt={1}>
        <Form.Label>{t`labels.email`}</Form.Label>
        <Input
          w="100%"
          type="email"
          placeholder={t`placeholders.email` || ''}
          onChange={onEmailTextChange}
          $errorType={formErrors[FormFields.Email]?.type}
        />
        <Form.Feedback $errorType={formErrors[FormFields.Email]?.type}>
          {formErrors[FormFields.Email]?.message}
        </Form.Feedback>
      </Form.Group>
      <Form.Group mt={1}>
        <Flex justifyContent="space-between">
          <Flex flex={1}>
            <Form.Label>{t`labels.password`}</Form.Label>
          </Flex>
          <Flex>
            <Form.Label>
              <Link
                to={RoutesPath.ForgotPassword}
              >{t`labels.forgotPassword`}</Link>
            </Form.Label>
          </Flex>
        </Flex>
        <Form.InputPassword
          w="100%"
          placeholder={t`placeholders.password` || ''}
          onChange={onPasswordTextChange}
          $errorType={formErrors[FormFields.Password]?.type}
        />
        <Form.Feedback $errorType={formErrors[FormFields.Password]?.type}>
          {formErrors[FormFields.Password]?.message}
        </Form.Feedback>
      </Form.Group>
      <Form.Group mt={4}>
        <Button
          w="100%"
          type="submit"
          disabled={loading || isViewerLoading}
          isLoading={loading || isViewerLoading}
        >
          {t`labels.signIn`}
        </Button>
      </Form.Group>
      <Form.Group mt={4}>
        <Form.Feedback $errorType={FormErrorTypes.Invalid}>
          {formState.errorSlug}
        </Form.Feedback>
      </Form.Group>
    </Form>
  );
}

export default SignInForm;
